/**
 * Joist Renderer - Draws joist bays on the floor plan canvas
 */

import { GRID_SIZE } from '../../../constants/grid';
import { JOIST_SIZES } from '../../../constants/structural';

const inchesToPixels = (inches) => inches * (GRID_SIZE / 6);

/**
 * Draw a single joist bay with parallel joist lines
 */
export function drawJoistBay(ctx, joist, scale, isSelected = false) {
  if (!ctx || !joist) return;

  const minX = Math.min(joist.startX, joist.endX);
  const maxX = Math.max(joist.startX, joist.endX);
  const minY = Math.min(joist.startY, joist.endY);
  const maxY = Math.max(joist.startY, joist.endY);
  const width = maxX - minX;
  const height = maxY - minY;

  if (width < 2 || height < 2) return;

  const spacingPx = inchesToPixels(joist.spacing || 16);
  if (spacingPx <= 0) return;
  if (!scale || scale <= 0) return;

  const color = joist.color || '#8B6914';
  const opacity = joist.opacity ?? 0.6;

  ctx.save();
  try {
    // Rim joist rectangle (dashed outline)
    ctx.globalAlpha = opacity;
    ctx.setLineDash([6 / scale, 4 / scale]);
    ctx.strokeStyle = isSelected ? '#00ffaa' : color;
    ctx.lineWidth = (isSelected ? 2 : 1.5) / scale;
    ctx.strokeRect(minX, minY, width, height);
    ctx.setLineDash([]);

    // Light fill for the bay area
    ctx.fillStyle = color;
    ctx.globalAlpha = opacity * 0.08;
    ctx.fillRect(minX, minY, width, height);
    ctx.globalAlpha = opacity;

    // Clip to bay rectangle for joist lines
    ctx.save();
    try {
      ctx.beginPath();
      ctx.rect(minX, minY, width, height);
      ctx.clip();

      // Draw parallel joist lines
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5 / scale;
      const maxLines = 500; // safety limit
      let count = 0;

      if (joist.direction === 'horizontal') {
        for (let y = minY + spacingPx / 2; y < maxY && count < maxLines; y += spacingPx) {
          ctx.beginPath();
          ctx.moveTo(minX, y);
          ctx.lineTo(maxX, y);
          ctx.stroke();
          count++;
        }
      } else {
        for (let x = minX + spacingPx / 2; x < maxX && count < maxLines; x += spacingPx) {
          ctx.beginPath();
          ctx.moveTo(x, minY);
          ctx.lineTo(x, maxY);
          ctx.stroke();
          count++;
        }
      }
    } finally {
      ctx.restore(); // always exit clip
    }

    // Direction arrow at center
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const arrowLen = Math.min(width, height) * 0.2;

    if (arrowLen > 10 / scale) {
      ctx.globalAlpha = opacity * 0.8;
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = 2 / scale;
      const headSize = 6 / scale;

      if (joist.direction === 'horizontal') {
        ctx.beginPath();
        ctx.moveTo(cx - arrowLen, cy);
        ctx.lineTo(cx + arrowLen, cy);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx + arrowLen, cy);
        ctx.lineTo(cx + arrowLen - headSize, cy - headSize * 0.6);
        ctx.lineTo(cx + arrowLen - headSize, cy + headSize * 0.6);
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(cx, cy - arrowLen);
        ctx.lineTo(cx, cy + arrowLen);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx, cy + arrowLen);
        ctx.lineTo(cx - headSize * 0.6, cy + arrowLen - headSize);
        ctx.lineTo(cx + headSize * 0.6, cy + arrowLen - headSize);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Label
    if (joist.showLabel && width > 60 / scale && height > 30 / scale) {
      try {
        const sizeInfo = JOIST_SIZES[joist.size];
        const label = sizeInfo
          ? `${sizeInfo.label} @ ${joist.spacing}" OC`
          : `${joist.size} @ ${joist.spacing}" OC`;

        const fontSize = Math.max(10, Math.min(13, Math.min(width, height) * scale / 8)) / scale;
        ctx.font = `600 ${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const labelY = cy + arrowLen + fontSize * 1.2;
        const metrics = ctx.measureText(label);
        const padX = 4 / scale;
        const padY = 2 / scale;

        // Background pill
        ctx.globalAlpha = 0.85;
        ctx.fillStyle = '#1a1f24';
        const pillW = metrics.width + padX * 2;
        const pillH = fontSize + padY * 2;
        ctx.fillRect(cx - pillW / 2, labelY - pillH / 2, pillW, pillH);

        // Text
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#b0c4a8';
        ctx.fillText(label, cx, labelY);
      } catch (e) {
        // Label drawing failed - skip it
      }
    }

    // Selection handles
    if (isSelected) {
      ctx.globalAlpha = 1;
      const hs = 5 / scale;
      ctx.fillStyle = '#00ffaa';
      const corners = [
        [minX, minY], [maxX, minY],
        [maxX, maxY], [minX, maxY],
      ];
      corners.forEach(([x, y]) => {
        ctx.fillRect(x - hs / 2, y - hs / 2, hs, hs);
      });
    }
  } catch (e) {
    // Catch any rendering errors to prevent crashing the React app
    console.warn('Joist rendering error:', e);
  } finally {
    ctx.restore();
  }
}

/**
 * Draw all joist bays
 */
export function drawAllJoists(ctx, joists, scale, selectedJoistId = null) {
  if (!ctx || !joists || joists.length === 0) return;
  try {
    joists.forEach(joist => {
      if (joist.id !== selectedJoistId) drawJoistBay(ctx, joist, scale, false);
    });
    const selected = joists.find(j => j.id === selectedJoistId);
    if (selected) drawJoistBay(ctx, selected, scale, true);
  } catch (e) {
    console.warn('drawAllJoists error:', e);
  }
}

/**
 * Draw preview while user is dragging to create a joist bay
 */
export function drawJoistPreview(ctx, startX, startY, endX, endY, direction, spacing, scale) {
  try {
    const preview = {
      startX, startY, endX, endY,
      direction: direction || 'horizontal',
      spacing: spacing || 16,
      color: '#4caf50',
      opacity: 0.3,
      showLabel: false,
    };
    drawJoistBay(ctx, preview, scale, false);
  } catch (e) {
    console.warn('drawJoistPreview error:', e);
  }
}

/**
 * Hit test — find which joist bay contains the given point
 */
export function hitTestJoist(x, y, joists) {
  if (!joists || joists.length === 0) return null;
  for (let i = joists.length - 1; i >= 0; i--) {
    const j = joists[i];
    const minX = Math.min(j.startX, j.endX);
    const maxX = Math.max(j.startX, j.endX);
    const minY = Math.min(j.startY, j.endY);
    const maxY = Math.max(j.startY, j.endY);
    if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
      return j;
    }
  }
  return null;
}
